import { Typography } from "@suraasa/placebo-ui"
import { Term } from "api/resources/analytics/types"
import clsx from "clsx"
import { Lock } from "iconoir-react"

import { renderAnalyticsMetric } from "./utils"

const TermBox = ({
  termNumber,
  progress = 0,
  isUnlocked = false,
}: {
  termNumber: number | string
  progress?: number
  isUnlocked?: boolean
}) => {
  const progressWidth = `${progress}%`
  const locked = !isUnlocked

  return (
    <div className="flex flex-1 flex-col items-center text-center">
      <div
        className={clsx(
          "relative flex h-[53px] w-full items-center justify-center overflow-hidden rounded-xl bg-onSurface-100",
          {
            "bg-primary-100": isUnlocked,
          }
        )}
      >
        {isUnlocked && (
          <div
            className="absolute left-0 top-0 h-full bg-primary-500 transition-all duration-500 ease-in-out"
            style={{ width: progressWidth }}
          />
        )}

        <div className="relative flex flex-col items-center justify-center">
          {locked ? (
            <>
              <Typography className="mb-0.5 text-preTitle text-onSurface-400">
                Term {termNumber}
              </Typography>
              <Lock
                fontSize={10}
                strokeWidth={2.5}
                className="text-onSurface-400"
              />
            </>
          ) : (
            <Typography
              className={clsx(
                "text-subtitle1 font-semibold text-onSurface-900",
                { "text-white": progress >= 55 }
              )}
            >
              {termNumber}
            </Typography>
          )}
        </div>
      </div>
    </div>
  )
}

interface ProgressTrackerProps {
  progress: number | undefined
  terms: Term[] | null
  filteredTerm: string
  filteredModule: string
  isCourse: boolean
}

const ProgressTracker = ({
  progress,
  terms,
  filteredTerm,
  filteredModule,
  isCourse,
}: ProgressTrackerProps) => {
  const isAnyTermUnlocked = terms?.some(term => term.unlocked) ?? false
  const unlockedTerms = terms?.map(term => term.unlocked) ?? []

  const shouldShowTermBoxes =
    Array.isArray(terms) &&
    terms.length > 0 &&
    isAnyTermUnlocked &&
    !(filteredModule || filteredTerm || isCourse)

  return (
    <div className="flex h-full flex-col justify-between p-2.5">
      <Typography className="mb-4 text-strong text-muted">Progress</Typography>

      <div>
        <div className="mb-2 flex items-baseline gap-1">
          {renderAnalyticsMetric(progress, ({ isNum, formattedValue }) => (
            <>
              <Typography
                className={clsx("text-title1 text-onSurface-900", {
                  "!font-thin text-onSurface-400": !isNum,
                })}
              >
                {formattedValue}
                {isNum && "%"}
              </Typography>
              {isNum && (
                <Typography className="text-strong text-onSurface-400">
                  Completed
                </Typography>
              )}
            </>
          ))}
        </div>

        <div className="flex gap-0.75 overflow-hidden">
          {shouldShowTermBoxes ? (
            terms.map((term, index) => {
              return (
                <div key={index} className="flex-1">
                  <TermBox
                    termNumber={term?.name ?? index + 1}
                    progress={term?.progress ?? 0}
                    isUnlocked={unlockedTerms[index] ?? false}
                  />
                </div>
              )
            })
          ) : progress ? (
            <div className="min-w-9 flex-1">
              <TermBox termNumber={""} progress={progress} isUnlocked={true} />
            </div>
          ) : (
            <></>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProgressTracker
